import { inject } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivateChildFn,
    Router,
    UrlTree,
} from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthUtils } from 'app/core/auth/auth.utils';
import {
    COMMON_ROUTES,
    ROUTE_ROLES,
    UserRole,
} from 'app/core/auth/role-routes';
import { RoleService } from 'app/core/auth/role.service';
import { Observable, map, of } from 'rxjs';

/**
 * C2 · Guard de rol por ruta (D51, D52, D53, D54, D59, D60, D61).
 *
 * Va en `canActivateChild` del bloque de negocio, nunca en `canActivate`: el
 * path del bloque es `''` y ahí la clave estaría vacía. `canActivateChild`
 * corre una vez por nivel, que es la granularidad que hace falta para
 * distinguir `/pacientes` de `/pacientes/:id`.
 *
 * Solo devuelve `true` o un `UrlTree`: nunca navega por su cuenta, nunca llama
 * a `signOut()` y nunca abre un snackbar. Denegar es ir a `/403`, no expulsar
 * (D54). `/403` y `/404` viven en un bloque sin guards, así que el destino de
 * la denegación no puede denegarse: no hay bucle posible.
 */

/** La clave de la matriz: el path de configuración, no la URL. */
export function routeKey(route: ActivatedRouteSnapshot): string {
    return route.pathFromRoot
        .map((snapshot) => snapshot.routeConfig?.path)
        .filter((path): path is string => !!path)
        .join('/');
}

/** Decisión para una clave y un rol, o para «sin rol» (`null`). */
export function isAllowed(key: string, role: UserRole | null): boolean {
    if (!role) {
        // D52 y D61: sin rol, rol no válido o `/user` caído, solo las comunes.
        return COMMON_ROUTES.includes(key);
    }

    // Fail-closed: una clave que no esté en la matriz se deniega.
    return (ROUTE_ROLES[key] ?? []).includes(role);
}

export const RoleGuard: CanActivateChildFn = (
    childRoute
): Observable<boolean | UrlTree> => {
    const router = inject(Router);
    const authService = inject(AuthService);
    const roleService = inject(RoleService);

    const key = routeKey(childRoute);
    const decide = (role: UserRole | null): boolean | UrlTree =>
        isAllowed(key, role) ? true : router.parseUrl('/403');

    // 1. Rol en memoria de las credenciales actuales: se decide sin pedir
    //    `/user`. Es el camino de casi todas las navegaciones. Se lee una sola
    //    vez: los tests cuentan invocaciones del guard por esa lectura.
    const cached = roleService.role;
    if (cached) {
        return of(decide(cached));
    }

    const token = authService.accessToken;

    // 2. Sin token no hay rol que usar, así que se cierra igual. No se
    //    devuelve `true`: ningún camino de este guard deja pasar sin haber
    //    comprobado el rol. Al usuario le llega de todos modos el `/sign-in`
    //    de `AuthGuard`, que corre a la vez y manda por ir primero en el array
    //    (prioritizedGuardValue). Pero si algún día `AuthGuard` dejara pasar
    //    una sesión sin token, aquí no se abriría nada.
    if (!token) {
        return of(decide(null));
    }

    // 3. Token caducado: el interceptor no lo adjunta, así que pedir `/user`
    //    daría 401 y arrastraría al usuario a `/sign-out`. Sin rol que usar, se
    //    cierra con el fallback de D61 en vez de abrir.
    if (AuthUtils.isTokenExpired(token)) {
        return of(decide(null));
    }

    // 4. Token válido y sin rol en memoria: primera navegación de esta carga.
    //    Una sola petición, compartida por los niveles de la navegación (D60).
    return roleService.role$().pipe(map(decide));
};
